import { Component, OnInit } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { Board } from "src/app/core/models/board.model";
import {
  FusionConnectDailyStat,
  FusionConnectRead,
  FusionConnectReadRequest,
} from "src/app/core/models/fusion-connect.model";
import { FusionConnectService } from "src/app/core/services/fusion-connect.service";
import { BoardService } from "src/app/core/services/board.service";

const DEFAULT_PAGE_SIZE = 50;

@Component({
  selector: "app-fusion-connect",
  templateUrl: "./fusion-connect.component.html",
  styleUrls: ["./fusion-connect.component.scss"],
})
export class FusionConnectComponent implements OnInit {
  breadCrumbItems = [{ label: "Fortune" }, { label: "Fusion Connect", active: true }];

  readForm: FormGroup;
  readsFilters: FormGroup;
  reads: FusionConnectRead[] = [];
  readsPage = { page: 0, size: DEFAULT_PAGE_SIZE, totalPages: 0, totalElements: 0 };

  manualStatForm: FormGroup;
  manualReads: FusionConnectRead[] = [];
  loadingManualReads = false;
  creatingManualStat = false;
  selectedManualStartIndex: number | null = null;
  selectedManualEndIndex: number | null = null;

  statsForm: FormGroup;
  stats: FusionConnectDailyStat[] = [];
  statsPage = { page: 0, size: DEFAULT_PAGE_SIZE, totalPages: 0, totalElements: 0 };

  boards: Board[] = [];
  private boardsMap = new Map<string, Board>();
  private isFusionConnectBoard = (b: any) =>
    b?.fusionConnect === true ||
    b?.fusionConnect === 1 ||
    b?.fusionConnect === "1" ||
    b?.fusionConnect === "true";
  loadingReads = false;
  loadingStats = false;
  submitting = false;
  showIngestTools = false;
  lastRefreshAt: Date = new Date();

  constructor(
    private fb: FormBuilder,
    private fusionConnectService: FusionConnectService,
    private boardService: BoardService,
    private toastr: ToastrService,
    private router: Router
  ) {
    this.readForm = this.fb.group({
      boardId: ["", Validators.required],
      deploymentId: [""],
      locationId: [""],
      gamePackageId: [""],
      coinIn: [0, [Validators.min(0)]],
      coinOut: [0, [Validators.min(0)]],
      cashIn: [0, [Validators.min(0)]],
      cashOut: [0, [Validators.min(0)]],
      gamesPlayed: [0, [Validators.min(0)]],
      gamesWon: [0, [Validators.min(0)]],
      timeStamp: [this.toLocalDateTimeInput()],
    });

    const { from, to } = this.defaultDateRange();
    this.statsForm = this.fb.group({
      boardId: [""],
      from: [from, Validators.required],
      to: [to, Validators.required],
      pageSize: [DEFAULT_PAGE_SIZE],
    });

    this.readsFilters = this.fb.group({
      boardId: [""],
      pageSize: [DEFAULT_PAGE_SIZE],
    });

    const { fromDateTime, toDateTime } = this.defaultManualDateTimeRange();
    this.manualStatForm = this.fb.group({
      boardId: ["", Validators.required],
      fromDateTime: [fromDateTime, Validators.required],
      toDateTime: [toDateTime, Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    await this.loadBoards();
    await Promise.all([this.loadReads(), this.loadDailyStats()]);
  }

  // ---------- data loading ----------
  async loadBoards(): Promise<void> {
    try {
      const boards = await this.boardService.getAll();
      this.boards = (boards || [])
        .filter((b) => this.isFusionConnectBoard(b))
        .sort((a, b) => {
          const aTag = (a?.assetTag || a?.serialNumber || "").toLowerCase();
          const bTag = (b?.assetTag || b?.serialNumber || "").toLowerCase();
          return aTag.localeCompare(bTag);
        });
      this.boardsMap.clear();
      this.boards.forEach((b) => b?.id && this.boardsMap.set(b.id, b));
    } catch (err) {
      console.error("[FusionConnect] failed to load boards", err);
      this.toastr.error("Could not load boards");
    }
  }

  async loadReads(page = 0): Promise<void> {
    this.loadingReads = true;
    try {
      const filters = this.readsFilters.value;
      const res = await this.fusionConnectService.fetchReads({
        page,
        pageSize: filters?.pageSize ?? DEFAULT_PAGE_SIZE,
        boardId: filters?.boardId || undefined,
      });
      this.reads = (res.content ?? []).map((r) => {
        const boardId = r.boardId || r.board?.id;
        const knownBoard = boardId ? this.boardsMap.get(boardId) : undefined;
        // enrich missing board/location fields for display
        if (!r.board && knownBoard) {
          r.board = knownBoard;
        }
        if (!r.location && (knownBoard?.location || knownBoard?.currentDeployment?.location)) {
          r.location = knownBoard.location || knownBoard.currentDeployment?.location;
        }
        return r;
      });
      this.readsPage = {
        page: res.number ?? page,
        size: res.size ?? DEFAULT_PAGE_SIZE,
        totalPages: res.totalPages ?? 0,
        totalElements: res.totalElements ?? this.reads.length,
      };
      this.lastRefreshAt = new Date();
    } catch (err) {
      console.error("[FusionConnect] failed to fetch reads", err);
      this.toastr.error("Failed to load live reads");
    } finally {
      this.loadingReads = false;
    }
  }

  async loadDailyStats(page = 0): Promise<void> {
    if (this.statsForm.invalid) {
      return;
    }
    this.loadingStats = true;
    try {
      const res = await this.fusionConnectService.fetchDailyStats({
        page,
        pageSize: this.statsForm.value?.pageSize ?? DEFAULT_PAGE_SIZE,
        boardId: this.statsForm.value?.boardId || undefined,
        from: this.statsForm.value.from,
        to: this.statsForm.value.to,
      });
      this.stats = res.content ?? [];
      this.statsPage = {
        page: res.number ?? page,
        size: res.size ?? DEFAULT_PAGE_SIZE,
        totalPages: res.totalPages ?? 0,
        totalElements: res.totalElements ?? this.stats.length,
      };
      this.lastRefreshAt = new Date();
    } catch (err) {
      console.error("[FusionConnect] failed to fetch daily stats", err);
      this.toastr.error("Failed to load daily stats");
    } finally {
      this.loadingStats = false;
    }
  }

  // ---------- actions ----------
  async submitRead(): Promise<void> {
    if (this.readForm.invalid || this.submitting) {
      return;
    }

    this.submitting = true;
    const value = this.readForm.value;
    const payload: FusionConnectReadRequest = {
      ...value,
      timeStamp: value.timeStamp ? this.toBackendTimestamp(value.timeStamp) : undefined,
    };

    try {
      await this.fusionConnectService.ingestRead(payload);
      this.toastr.success("Live read sent");
      await this.loadReads(0);
    } catch (err) {
      console.error("[FusionConnect] failed to submit read", err);
      this.toastr.error("Failed to send live read");
    } finally {
      this.submitting = false;
    }
  }

  async loadManualStatReads(): Promise<void> {
    if (this.manualStatForm.invalid || this.loadingManualReads) {
      return;
    }

    const values = this.manualStatForm.value;
    const from = this.parseDate(values.fromDateTime);
    const to = this.parseDate(values.toDateTime);

    if (!from || !to) {
      this.toastr.warning("Please choose a valid date/time range");
      return;
    }

    if (from >= to) {
      this.toastr.warning("The end time must be after the start time");
      return;
    }

    this.loadingManualReads = true;
    try {
      const boardId = values.boardId;
      const res = await this.fusionConnectService.fetchReads({
        page: 0,
        pageSize: 1000,
        boardId,
      });

      const reads = (res.content ?? [])
        .map((r) => this.enrichReadForDisplay(r))
        .filter((r) => {
          const ts = this.parseDate(r?.timeStamp);
          return !!ts && ts >= from && ts <= to;
        })
        .sort((a, b) => {
          const aTs = this.parseDate(a?.timeStamp)?.getTime() ?? 0;
          const bTs = this.parseDate(b?.timeStamp)?.getTime() ?? 0;
          return aTs - bTs;
        });

      this.manualReads = reads;
      this.selectedManualStartIndex = null;
      this.selectedManualEndIndex = null;

      if (!reads.length) {
        this.toastr.info("No live reads found for that board and date/time window");
      }
    } catch (err) {
      console.error("[FusionConnect] failed to load manual stat reads", err);
      this.toastr.error("Failed to load reads for manual stat creation");
    } finally {
      this.loadingManualReads = false;
    }
  }

  selectManualStart(index: number): void {
    this.selectedManualStartIndex = index;
  }

  selectManualEnd(index: number): void {
    this.selectedManualEndIndex = index;
  }

  async createBoardStatFromSelectedReads(): Promise<void> {
    if (this.creatingManualStat) {
      return;
    }

    if (this.selectedManualStartIndex === null || this.selectedManualEndIndex === null) {
      this.toastr.warning("Select both a start read and an end read");
      return;
    }

    const startRead = this.manualReads[this.selectedManualStartIndex];
    const endRead = this.manualReads[this.selectedManualEndIndex];
    if (!startRead || !endRead) {
      this.toastr.warning("The selected reads are not valid");
      return;
    }

    const startTs = this.parseDate(startRead.timeStamp);
    const endTs = this.parseDate(endRead.timeStamp);

    if (!startTs || !endTs || endTs <= startTs) {
      this.toastr.warning("End read must be later than start read");
      return;
    }

    if (!startRead.id || !endRead.id) {
      this.toastr.error("Selected reads are missing ids");
      return;
    }

    this.creatingManualStat = true;
    try {
      const created = await this.fusionConnectService.createRegularBoardStatFromLiveReads({
        startReadId: startRead.id,
        endReadId: endRead.id,
      });

      if (!created) {
        this.toastr.error("Board stat was not created");
        return;
      }

      this.toastr.success(
        "Regular Board Stats row created. It will appear under Board Stats, not Konnect Live Stats."
      );
      await this.loadDailyStats(0);
    } catch (err: any) {
      console.error("[FusionConnect] failed to create board stat from live reads", err);
      const httpErr = err as HttpErrorResponse;
      const backendMessage = httpErr?.error?.message || httpErr?.message;
      this.toastr.error(backendMessage || "Failed to create board stat from selected reads");
    } finally {
      this.creatingManualStat = false;
    }
  }

  refreshReads(): void {
    this.loadReads(this.readsPage.page);
  }

  refreshAll(): void {
    this.loadReads(this.readsPage.page);
    this.loadDailyStats(this.statsPage.page);
  }

  refreshStats(): void {
    this.loadDailyStats(this.statsPage.page);
  }

  openStatsWorkspace(): void {
    this.router.navigate(['/fusion-connect-stats']);
  }

  toggleIngestTools(): void {
    this.showIngestTools = !this.showIngestTools;
  }

  nextReadsPage(): void {
    if (this.readsPage.page + 1 < this.readsPage.totalPages) {
      this.loadReads(this.readsPage.page + 1);
    }
  }

  prevReadsPage(): void {
    if (this.readsPage.page > 0) {
      this.loadReads(this.readsPage.page - 1);
    }
  }

  nextStatsPage(): void {
    if (this.statsPage.page + 1 < this.statsPage.totalPages) {
      this.loadDailyStats(this.statsPage.page + 1);
    }
  }

  prevStatsPage(): void {
    if (this.statsPage.page > 0) {
      this.loadDailyStats(this.statsPage.page - 1);
    }
  }

  // ---------- helpers ----------
  trackById = (_: number, item: any) => item?.id || item?.boardId || item;

  resetTimestamp(): void {
    this.readForm.patchValue({ timeStamp: this.toLocalDateTimeInput() });
  }

  get liveBoardCount(): number {
    return new Set((this.reads || []).map((r) => r.boardId || r.board?.id).filter(Boolean)).size;
  }

  get liveLocationCount(): number {
    return new Set((this.reads || []).map((r) => r.location?.id || r.board?.location?.id).filter(Boolean)).size;
  }

  get todayCashIn(): number {
    return (this.stats || []).reduce((sum, item) => sum + Number(item?.cashIn || 0), 0);
  }

  get lastReadTimestamp(): string {
    return this.reads?.[0]?.timeStamp || '';
  }

  getReadGameName(read: FusionConnectRead): string {
    return (
      read?.gamePackageName ||
      read?.gamePackage?.name ||
      read?.board?.installedGamePackage?.name ||
      read?.board?.currentDeployment?.installedGamePackage?.name ||
      'N/A'
    );
  }

  getReadGameVersion(read: FusionConnectRead): string {
    return (
      read?.gamePackageVersion ||
      read?.gamePackage?.version ||
      read?.board?.installedGamePackage?.version ||
      read?.board?.currentDeployment?.installedGamePackage?.version ||
      ''
    );
  }

  formatReadKey(read: FusionConnectRead): string {
    const boardId = read.boardId || read.board?.id || "";
    return `${boardId}-${read.timeStamp}`;
  }

  private enrichReadForDisplay(r: FusionConnectRead): FusionConnectRead {
    const boardId = r.boardId || r.board?.id;
    const knownBoard = boardId ? this.boardsMap.get(boardId) : undefined;

    if (!r.board && knownBoard) {
      r.board = knownBoard;
    }
    if (!r.location && (knownBoard?.location || knownBoard?.currentDeployment?.location)) {
      r.location = knownBoard.location || knownBoard.currentDeployment?.location;
    }
    return r;
  }

  private parseDate(value?: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private defaultManualDateTimeRange(): { fromDateTime: string; toDateTime: string } {
    const to = new Date();
    const from = new Date(to);
    from.setHours(from.getHours() - 24);
    return {
      fromDateTime: this.toLocalDateTimeInput(from),
      toDateTime: this.toLocalDateTimeInput(to),
    };
  }

  private defaultDateRange(): { from: string; to: string } {
    const today = new Date();
    const to = today.toISOString().slice(0, 10);
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    const from = start.toISOString().slice(0, 10);
    return { from, to };
  }

  private toLocalDateTimeInput(date = new Date()): string {
    const tzOffset = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffset);
    return local.toISOString().slice(0, 16);
  }

  private toBackendTimestamp(input: string): string {
    const date = new Date(input);
    const tzOffset = date.getTimezoneOffset() * 60000;
    const local = new Date(date.getTime() - tzOffset);
    return local.toISOString().slice(0, 19);
  }
}
